import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Topbar from "../components/Topbar";
import { createUser, getSingleUser, updateUser, removeUserImage } from "../services/userService";

const FormInput = ({ label, id, type = "text", placeholder, value, onChange, required = false, disabled = false }) => (
    <div className="form-group">
        <label className="form-label" htmlFor={id}>
            {label} {required && <span className="req">*</span>}
        </label>
        <input
            className="form-input"
            id={id}
            name={id}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            style={disabled ? { backgroundColor: '#F8FAFC', cursor: 'not-allowed', color: 'var(--grey)' } : {}}
        />
    </div>
);

const ToggleField = ({ label, value, onChange, sub, onLabel = 'On', offLabel = 'Off' }) => (
    <div className="toggle-row" style={{ marginTop: 8 }}>
        <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>{sub}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--grey)' }}>{(value === 1 || value === true) ? onLabel : offLabel}</span>
            <button
                type="button"
                onClick={onChange}
                className={`toggle-switch ${(value === 1 || value === true) ? 'on' : 'off'}`}
            />
        </div>
    </div>
);

export default function ManageUser() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [formData, setFormData] = useState({
        full_name: "",
        email: "",
        phone_number: "",
        password: "",
        confirm_password: "",
        is_email_verified: false,
        status: 1,
        type: "Free"
    });
    const [profileImage, setProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [isRemovingImage, setIsRemovingImage] = useState(false);

    useEffect(() => {
        if (isEdit) {
            fetchUserData();
        }
    }, [id]);

    const fetchUserData = async () => {
        setIsLoading(true);
        try {
            const data = await getSingleUser(id);
            if (data && data.data) {
                const user = data.data;
                const mappedData = {
                    full_name: user.full_name || "",
                    email: user.email || "",
                    phone_number: user.phone_number || "",
                    is_email_verified: !!user.is_email_verified,
                    status: user.status ?? 1,
                    type: user.type || (user.account_type === 1 ? "Premium" : "Free"),
                    password: "",
                    confirm_password: ""
                };
                setFormData(mappedData);
                setInitialData(mappedData);
                // Load existing profile image preview
                const existingImage = user.profile_image || user.image;
                if (existingImage) {
                    setProfileImagePreview(existingImage);
                }
            }
        } catch (error) {
            console.log("Failed to load user data : ", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error("Please select a valid image file");
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image size must be less than 5MB");
            return;
        }

        setProfileImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setProfileImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = async () => {
        if (isEdit && profileImagePreview && !profileImage) {
            // Remove existing image from server
            setIsRemovingImage(true);
            try {
                await removeUserImage(id);
                setProfileImagePreview(null);
                toast.success("Image removed successfully");
            } catch (error) {
                console.log("Failed to remove image:", error);
                toast.error("Failed to remove image");
            } finally {
                setIsRemovingImage(false);
            }
        } else {
            // Just clear the preview
            setProfileImage(null);
            setProfileImagePreview(null);
        }
    };

    const handleToggle = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: field === 'status'
                ? (prev[field] === 1 ? 2 : 1)
                : field === 'type'
                    ? (prev[field] === 'Premium' ? 'Free' : 'Premium')
                    : (typeof prev[field] === 'boolean' ? !prev[field] : (prev[field] === 1 ? 0 : 1))
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.full_name.length < 3) return toast.error("Full Name must be at least 3 characters");
        if (!formData.email.includes("@")) return toast.error("Invalid email address");

        if (!isEdit && formData.password.length < 8) return toast.error("Password must be at least 8 characters");

        if (formData.password && formData.password !== formData.confirm_password) {
            return toast.error("Passwords do not match");
        }

        setIsLoading(true);
        try {
            if (isEdit) {
                // Determine changed values only
                const payload = {};
                Object.keys(formData).forEach(key => {
                    if (key === 'email' || key === 'confirm_password') return;
                    
                    if (key === 'password') {
                        if (formData.password.length > 0) {
                            payload.password = formData.password;
                            payload.confirm_password = formData.confirm_password;
                        }
                        return;
                    }

                    if (formData[key] !== initialData[key]) {
                        payload[key] = formData[key];
                    }
                });

                // Add image if changed
                if (profileImage) {
                    payload.profile_image = profileImage;
                    payload.image = profileImage;
                }

                if (Object.keys(payload).length === 0 && !profileImage) {
                    setIsLoading(false);
                    return toast.info("No changes detected");
                }

                // Use FormData if there's an image, otherwise use regular JSON
                let updateData = payload;
                if (profileImage) {
                    updateData = new FormData();
                    Object.keys(payload).forEach(key => {
                        updateData.append(key, payload[key]);
                    });
                    updateData.append('image', profileImage);
                }

                await updateUser(id, updateData);
                toast.success("User updated successfully");
            } else {
                const { confirm_password, is_email_verified, ...payload } = formData;
                
                let createData = payload;
                if (profileImage) {
                    createData = new FormData();
                    Object.keys(payload).forEach(key => {
                        createData.append(key, payload[key]);
                    });
                    createData.append('image', profileImage);
                }

                await createUser(createData);
                toast.success("User created successfully");
            }
            navigate("/users");
        } catch (error) {
            console.log("Failed to create or update user : ", error);
            // toast.error(isEdit ? "Failed to update user" : "Failed to create user");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ background: '#F0F4F8', minHeight: '100vh' }}>
            <Topbar
                title={isEdit ? "Update User" : "Create New User"}
                subtitle={isEdit ? `Modifying user account: ${formData.email}` : "Add a new user to the GateGuide system"}
            />

            <div className="content">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <button
                        onClick={() => navigate("/users")}
                        className="btn btn-outline"
                        style={{ padding: '8px 12px' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back to List
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="screens-stack">
                    {/* PERSONAL INFORMATION & USER PHOTO */}
                    <div className="card form-2col">
                        <div className="form-main">
                            <div className="fsec-title">1. Personal Information</div>
                            <div className="form-grid">
                                <FormInput
                                    label="Full Name"
                                    id="full_name"
                                    placeholder="Enter user's full name"
                                    value={formData.full_name}
                                    onChange={handleChange}
                                    required
                                />
                                <FormInput
                                    label="Email Address"
                                    id="email"
                                    type="email"
                                    placeholder="user@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    disabled={isEdit}
                                />
                                <FormInput
                                    label="Phone Number"
                                    id="phone_number"
                                    placeholder="+1 (555) 000-0000"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                />
                                {!isEdit && (
                                    <>
                                        <FormInput
                                            label="Password"
                                            id="password"
                                            type="password"
                                            placeholder="Create a secure password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required
                                        />
                                        <FormInput
                                            label="Confirm Password"
                                            id="confirm_password"
                                            type="password"
                                            placeholder="Re-enter password"
                                            value={formData.confirm_password}
                                            onChange={handleChange}
                                            required
                                        />
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="form-aside">
                            <div className="fsec-title">User Photo / Image</div>
                            <div
                                className="photo-upload"
                                style={{
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: profileImagePreview ? '1.5px solid var(--blue)' : '1.5px dashed #CBD5E1',
                                    background: profileImagePreview ? '#F8FAFC' : 'var(--light)',
                                    borderRadius: 12,
                                    padding: profileImagePreview ? '8px' : '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 130,
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => document.getElementById('profile_image_input').click()}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.style.borderColor = 'var(--blue)';
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.style.borderColor = profileImagePreview ? 'var(--blue)' : '#CBD5E1';
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const files = e.dataTransfer.files;
                                    if (files.length > 0) {
                                        handleImageChange({ target: { files } });
                                    }
                                }}
                            >
                                {profileImagePreview ? (
                                    <div style={{ position: 'relative', width: '100%', height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={profileImagePreview} alt="Preview" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }} />
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveImage();
                                            }}
                                            disabled={isRemovingImage}
                                            style={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                background: '#EF4444',
                                                color: '#FFFFFF',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: 22,
                                                height: 22,
                                                fontSize: 12,
                                                fontWeight: 'bold',
                                                cursor: isRemovingImage ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 10,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                                                lineHeight: 1
                                            }}
                                            title="Remove Image"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                        <div className="photo-upload-label" style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                                            Upload User Image
                                        </div>
                                        <div style={{ fontSize: 10, color: 'var(--grey)', marginTop: 2 }}>
                                            JPG, PNG • Max 5MB
                                        </div>
                                    </>
                                )}
                                <input
                                    type="file"
                                    id="profile_image_input"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ACCOUNT SETTINGS */}
                    <div className="card">
                        <div className="form-main">
                            <div className="fsec-title">3. Account Preferences & Status</div>
                            <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                                <ToggleField
                                    label="Email Verified"
                                    value={formData.is_email_verified}
                                    onChange={() => handleToggle('is_email_verified')}
                                    sub="Manually grant verified status"
                                />
                                <ToggleField
                                    label="Account Status"
                                    value={formData.status}
                                    onChange={() => handleToggle('status')}
                                    sub="Enable or disable user access"
                                />
                                <ToggleField 
                                    label="Account Tier" 
                                    value={formData.type === 'Premium'} 
                                    onChange={() => handleToggle('type')}
                                    sub="Toggle between Free and Premium tiers"
                                    onLabel="Premium"
                                    offLabel="Free"
                                />
                            </div>
                        </div>
                    </div>

                    {/* SECURITY UPDATES */}
                    {isEdit && (
                        <div className="card" style={{ borderLeft: '4px solid var(--orange)' }}>
                            <div className="form-main">
                                <div className="fsec-title" style={{ color: 'var(--orange)' }}>4. Security & Password Update (Optional)</div>
                                <div style={{ fontSize: 12, color: 'var(--grey)', marginBottom: 20, marginTop: -10 }}>
                                    Leave these fields empty if you do not wish to change the user's password.
                                </div>
                                <div className="form-grid">
                                    <FormInput
                                        label="New Password"
                                        id="password"
                                        type="password"
                                        placeholder="Enter new secure password"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                    <FormInput
                                        label="Confirm New Password"
                                        id="confirm_password"
                                        type="password"
                                        placeholder="Repeat new password"
                                        value={formData.confirm_password}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ACTIONS */}
                    <div className="form-footer" style={{ background: 'white', borderRadius: 16, border: '1px solid #E8EDF3', padding: 20 }}>
                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => navigate("/users")}
                            style={{ minWidth: 120, justifyContent: 'center' }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading}
                            style={{ minWidth: 160, justifyContent: 'center', height: 44, fontSize: 14 }}
                        >
                            {isLoading ? (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                                        <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                    </svg>
                                    Processing...
                                </>
                            ) : (
                                isEdit ? "Update User Account" : "Create User Account"
                            )}
                        </button>
                    </div>
                </form>

                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    
                    @media (max-width: 768px) {
                        .form-grid { 
                            grid-template-columns: 1fr !important; 
                            gap: 15px !important;
                        }
                        .content { padding: 15px !important; }
                        .form-footer { 
                            flex-direction: column; 
                            gap: 10px; 
                        }
                        .form-footer button { width: 100% !important; }
                        .profile-layout { flex-direction: column !important; }
                        .fsec-title { font-size: 16px !important; }
                        .card { padding: 15px !important; }
                    }
                `}</style>
            </div>
        </div>
    );
}
